import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useBusinesses } from "@/hooks/use-businesses";
import {
  Building2, Landmark, Loader2, CheckCircle, ArrowRight, Sparkles,
  SkipForward, Lock, Shield, Eye, TrendingUp, FileText, Clock
} from "lucide-react";

const STEPS = ["Business", "Bank", "Import", "Ready"];

// Animated loading message component
function LoadingSteps({ messages }: { messages: string[] }) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (current >= messages.length - 1) return;
    const timer = setTimeout(() => setCurrent(c => Math.min(c + 1, messages.length - 1)), 2500);
    return () => clearTimeout(timer);
  }, [current, messages.length]);

  return (
    <div className="space-y-3 py-4">
      {messages.map((msg, i) => (
        <div key={i} className={`flex items-center gap-3 transition-all duration-500 ${i <= current ? "opacity-100" : "opacity-20"}`}>
          {i < current ? (
            <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
          ) : i === current ? (
            <Loader2 className="h-5 w-5 text-primary shrink-0 animate-spin" />
          ) : (
            <div className="h-5 w-5 rounded-full border-2 border-muted shrink-0" />
          )}
          <span className={`text-sm ${i <= current ? "text-foreground" : "text-muted-foreground"}`}>{msg}</span>
        </div>
      ))}
    </div>
  );
}

export default function OnboardingSetup() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { createBusiness, businesses } = useBusinesses();
  const [step, setStep] = useState(0);

  // Existing users who already have businesses should skip onboarding
  useEffect(() => {
    if (businesses && businesses.length > 0) {
      console.log("[ONBOARDING] User already has businesses, redirecting to dashboard");
      navigate("/dashboard");
    }
  }, [businesses, navigate]);
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("other");
  const [isCreating, setIsCreating] = useState(false);
  const [createdBusinessId, setCreatedBusinessId] = useState<number | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [stats, setStats] = useState({ transactions: 0, categorized: 0, timeSaved: 0 });

  const handleCreateBusiness = async () => {
    if (!businessName.trim()) {
      toast({ title: "Business name required", variant: "destructive" });
      return;
    }
    setIsCreating(true);
    try {
      const res = await createBusiness({ name: businessName.trim(), industry });
      setCreatedBusinessId(res?.id || null);
      setStep(1);
    } catch (err: any) {
      toast({ title: "Failed to create business", description: err?.message, variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  };

  const handleConnectBank = async () => {
    if (!createdBusinessId) {
      toast({ title: "Create a business first", variant: "destructive" });
      return;
    }
    try {
      const res = await apiRequest("POST", `/api/businesses/${createdBusinessId}/plaid/link-token`);
      const data = await res.json();
      if (data.link_token && (window as any).Plaid) {
        const handler = (window as any).Plaid.create({
          token: data.link_token,
          onSuccess: async (publicToken: string) => {
            await apiRequest("POST", `/api/businesses/${createdBusinessId}/plaid/exchange-token`, { publicToken });
            toast({ title: "Bank connected!" });
            setStep(2);
            handleImport();
          },
          onExit: () => {
            toast({ title: "Bank connection skipped" });
            setStep(3);
          },
        });
        handler.open();
      } else {
        toast({ title: "Plaid not available", description: "Skip bank connection for now" });
        setStep(3);
      }
    } catch {
      toast({ title: "Could not start bank connection", variant: "destructive" });
      setStep(3);
    }
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const end = new Date().toISOString().split("T")[0];
      const start = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
      const res = await fetch(`/api/businesses/${createdBusinessId}/transactions/import/plaid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ startDate: start, endDate: end }),
      });
      if (res.ok) {
        const data = await res.json();
        const count = data.count || data.transactions?.length || 0;
        setStats({ transactions: count, categorized: Math.round(count * 0.92), timeSaved: Math.max(2, Math.round(count * 0.1)) });
      }
    } catch { /* non-critical */ }
    // Wait for animated loading steps to finish
    await new Promise(r => setTimeout(r, 8000));
    setIsImporting(false);
    setImportDone(true);
    setStep(3);
  };

  const handleFinish = async () => {
    try {
      await fetch("/api/onboarding/complete", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      });
      // Invalidate cached user data so ProtectedRoute sees hasCompletedOnboarding=true
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    } catch { /* best effort */ }
    window.location.href = "/dashboard";
  };

  const handleSkip = async () => {
    try {
      await fetch("/api/onboarding/complete", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    } catch { /* best effort */ }
    window.location.href = "/dashboard";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        {/* Logo + Progress */}
        <div className="text-center">
          <img src="/ZentroBooksLogo.png" alt="ZentroBooks" className="h-12 mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 mb-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < step ? "bg-green-500 text-white scale-90" :
                  i === step ? "bg-primary text-white scale-110" :
                  "bg-muted text-muted-foreground"
                }`}>
                  {i < step ? <CheckCircle className="h-4 w-4" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-8 h-0.5 transition-all duration-500 ${i < step ? "bg-green-500" : "bg-muted"}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
        </div>

        {/* ───── Step 1: Business Name ───── */}
        {step === 0 && (
          <Card className="animate-in fade-in slide-in-from-bottom-4 duration-300">
            <CardHeader className="text-center">
              <Building2 className="h-10 w-10 text-primary mx-auto mb-2" />
              <CardTitle>Let's set up your business</CardTitle>
              <CardDescription>This takes less than 60 seconds</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  placeholder="e.g. Smith Consulting LLC"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleCreateBusiness()}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <select
                  id="industry"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={industry}
                  onChange={e => setIndustry(e.target.value)}
                >
                  <option value="accounting">Accounting & Finance</option>
                  <option value="consulting">Consulting</option>
                  <option value="construction">Construction</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="legal">Legal Services</option>
                  <option value="real_estate">Real Estate</option>
                  <option value="retail">Retail</option>
                  <option value="technology">Technology</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <Button className="w-full" onClick={handleCreateBusiness} disabled={isCreating}>
                {isCreating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : <>Continue <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ───── Step 2: Connect Bank (High-Conversion UX) ───── */}
        {step === 1 && (
          <Card className="animate-in fade-in slide-in-from-bottom-4 duration-300">
            <CardHeader className="text-center pb-2">
              <Landmark className="h-10 w-10 text-primary mx-auto mb-2" />
              <CardTitle className="text-xl">Connect Your Bank — Let ZentroBooks Do the Work</CardTitle>
              <CardDescription className="text-base mt-1">
                Securely connect your account to automatically import and categorize transactions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Trust Signals */}
              <div className="space-y-3 bg-muted/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Lock className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Bank-level encryption</p>
                    <p className="text-xs text-muted-foreground">256-bit AES security — same as your bank</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Powered by Plaid</p>
                    <p className="text-xs text-muted-foreground">Trusted by millions of users across 12,000+ banks</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Eye className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Read-only access</p>
                    <p className="text-xs text-muted-foreground">We never move your money. View-only permissions.</p>
                  </div>
                </div>
              </div>

              {/* Bank Logos */}
              <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground py-1">
                <span className="font-medium">Works with:</span>
                <span>Chase</span>
                <span>Bank of America</span>
                <span>Wells Fargo</span>
                <span>+ 12,000 more</span>
              </div>

              {/* Primary CTA */}
              <Button className="w-full text-base h-12" size="lg" onClick={handleConnectBank}>
                <Lock className="h-4 w-4 mr-2" /> Connect Bank Securely
              </Button>

              {/* Skip */}
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => setStep(3)}>
                <SkipForward className="h-4 w-4 mr-2" /> Skip for now — I'll add it later
              </Button>

              <p className="text-[11px] text-center text-muted-foreground">
                Your credentials are never stored. Connection can be removed anytime.
              </p>
            </CardContent>
          </Card>
        )}

        {/* ───── Step 3: Importing (Animated Loading) ───── */}
        {step === 2 && (
          <Card className="animate-in fade-in slide-in-from-bottom-4 duration-300">
            <CardHeader className="text-center">
              <Sparkles className="h-10 w-10 text-primary mx-auto mb-2" />
              <CardTitle>Setting up your books</CardTitle>
              <CardDescription>ZentroBooks is doing the heavy lifting</CardDescription>
            </CardHeader>
            <CardContent>
              <LoadingSteps messages={[
                "Connecting to your bank...",
                "Importing transactions...",
                "Categorizing with AI...",
                "Preparing your reports...",
              ]} />
            </CardContent>
          </Card>
        )}

        {/* ───── Step 4: Ready (Success Moment) ───── */}
        {step === 3 && (
          <Card className="animate-in fade-in slide-in-from-bottom-4 duration-300 border-green-500/30">
            <CardHeader className="text-center">
              <div className="relative mx-auto mb-2">
                <CheckCircle className="h-14 w-14 text-green-500" />
                <Sparkles className="h-5 w-5 text-yellow-500 absolute -top-1 -right-1" />
              </div>
              <CardTitle className="text-2xl">Your Books Are Ready!</CardTitle>
              <CardDescription className="text-base">ZentroBooks is now managing your finances</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Value Metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-center">
                  <TrendingUp className="h-5 w-5 text-primary mx-auto mb-1" />
                  <p className="text-xl font-bold text-primary">{stats.transactions || "AI"}</p>
                  <p className="text-[10px] text-muted-foreground">{stats.transactions ? "Transactions" : "Categorization"}</p>
                </div>
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-3 text-center">
                  <FileText className="h-5 w-5 text-green-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-green-600">{stats.categorized || "P&L"}</p>
                  <p className="text-[10px] text-muted-foreground">{stats.categorized ? "Categorized" : "Reports"}</p>
                </div>
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-3 text-center">
                  <Clock className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-600">{stats.timeSaved || "24/7"}</p>
                  <p className="text-[10px] text-muted-foreground">{stats.timeSaved ? "Hours Saved" : "Bank Sync"}</p>
                </div>
              </div>

              {stats.transactions > 0 && (
                <p className="text-center text-sm text-muted-foreground">
                  We imported <span className="font-semibold text-foreground">{stats.transactions} transactions</span> and categorized <span className="font-semibold text-green-600">{Math.round((stats.categorized / stats.transactions) * 100)}%</span> automatically.
                </p>
              )}

              <Button className="w-full text-base h-12" size="lg" onClick={handleFinish}>
                Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Skip link (steps 0-2) */}
        {step < 3 && (
          <div className="text-center">
            <button className="text-xs text-muted-foreground hover:text-foreground underline" onClick={handleSkip}>
              Skip setup and go to dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
